import { z } from "zod";
import { EcoLabel, EnvironmentalCategory, EnvironmentalUsedFor, WasteDisposalDestination, wasteDisposalDestinationToString } from "../environmentalCommitment";
export { WasteDisposalDestination, wasteDisposalDestinationToString };
export var WaterWithdrawn;
(function (WaterWithdrawn) {
    WaterWithdrawn["Withdrawn"] = "water-withdrawn_withdrawn";
    WaterWithdrawn["Discharged"] = "water-withdrawn_discharged";
})(WaterWithdrawn || (WaterWithdrawn = {}));
/**
 * Withdrawn vs discharged follows from the category alone — Water Discharge
 * is discharged, everything else is withdrawn — so the reporter is shown the
 * answer rather than asked for it.
 */
export const waterWithdrawnForCategory = (category) => category === EnvironmentalCategory.WaterDischarge
    ? WaterWithdrawn.Discharged
    : WaterWithdrawn.Withdrawn;
export const waterWithdrawnToString = (w) => {
    switch (w) {
        case WaterWithdrawn.Withdrawn:
            return "Withdrawn";
        case WaterWithdrawn.Discharged:
            return "Discharged";
    }
};
export const environmentalReportSubmissionSchema = z.object({
    contractor_package_id: z.string(),
    created: z.date(),
    /**
     * The factor values this row was costed with, keyed by `EmissionFactorType`.
     *
     * READ-ONLY from the client's point of view, which is why it appears on the
     * entity (the database row shape) and deliberately NOT on the line item the
     * UI edits: the column exists so a reported figure cannot change after the
     * fact, and a value the client could send is a value the client could forge.
     * The backend resolves it on write and ignores whatever arrives here.
     *
     * Absent means never stamped — a row written before the column existed —
     * and reads fall back to the project's live factors. An empty object is a
     * different answer: costed, and nothing applied.
     */
    emission_factors_applied: z.record(z.string(), z.number()).optional(),
    energy_water_used_for: z.enum(EnvironmentalUsedFor).optional(),
    /** What `Other` means; kept only while the use is `Other`. */
    energy_water_used_for_description: z.string().optional(),
    environmental_report_id: z.string(),
    /** Where the entry happened, as written on the invoice or docket. */
    location: z.string().optional(),
    materials_abn: z.string().optional(),
    /**
     * The eco-labels this product carries. Asked on EVERY materials row, target
     * or no target — it is an intrinsic property of the product and the reason
     * the client asked for it first ("an important part of the data
     * classification"). `[]` is a real answer: uncertified.
     */
    materials_eco_labels: z.array(z.enum(EcoLabel)).optional(),
    /** The supplier's mix or product code, where it has one. */
    materials_mix_code: z.string().optional(),
    materials_name: z.string().optional(),
    materials_supplier_name: z.string().optional(),
    /**
     * The row's invoice value in dollars, EXCLUDING GST — the same basis as
     * `social_spend.invoice_value`, so a share cannot mix GST bases.
     *
     * ABSENT MEANS UNPRICED, and is never defaulted to 0. This is the one place
     * the rule is argued; everywhere else cites it. A value-weighted share drops
     * an unpriced row from BOTH sides and still reads as a confident percentage,
     * so `unpricedRowCount` is the only thing that makes the gap visible — and a
     * zero default would make every historical row look priced and report that
     * count as 0, the same trap `quantity` (`notNull().default(0)`) already has.
     *
     * The DB column is nullable; the decoder maps NULL to `undefined`. Collected
     * only when a Value target reads it (`isMaterialValueCollected`).
     */
    materials_value: z.number().optional(),
    project_environmental_commitment_id: z.string(),
    project_id: z.string(),
    quantity: z.number().optional(),
    recycled_pct: z.number().optional(),
    report_id: z.string(),
    transport_total_distance: z.number().optional(),
    transport_total_freight: z.number().optional(),
    uri: z.string().optional(),
    waste_disposal_destination: z.enum(WasteDisposalDestination).optional(),
    waste_disposal_facility_name: z.string().optional(),
    waste_docket_number: z.string().optional(),
    waste_hazardous: z.boolean().optional(),
    waste_transporter_company: z.string().optional(),
    water_withdrawn: z.enum(WaterWithdrawn).optional()
});
export const createDefaultEnvironmentalReportSubmission = (projectEnvironmentalCommitmentId, reportId, contractorPackageId, projectId) => ({
    contractor_package_id: contractorPackageId,
    created: new Date(),
    environmental_report_id: "",
    project_environmental_commitment_id: projectEnvironmentalCommitmentId,
    project_id: projectId,
    quantity: 0,
    report_id: reportId
});
